// /car — Transportation Hub (iter82, iter87).
//
// Central hub for ride requests. iter87: all location taps fire
// sendRideRequest() directly (WhatsApp opens, no popup).
//
// Sections (top → bottom):
//   1. Header: TRANSPORTATION eyebrow, "Where to?" title ("to?" gold italic)
//   2. Local search input — filters hotel + saved businesses + custom locations
//   3. Staying-At hotel card (gold border) — or save-hotel prompt
//   4. Saved Places — horizontal scroll of saved businesses + custom locations
//   5. Recent Rides — list of past destinations (localStorage habano.ride.history)
//   6. Find on Map button — navigates to /car/map
//
// Search filters locally — no internet required.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Map as MapIcon, Car as CarIcon, Bed, Gem, Clock, ChevronRight } from "lucide-react";
import { api, getMe } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import HeaderActions from "../components/HeaderActions";
import { useI18n } from "../lib/i18n";
import { lsGet, lsSet } from "../lib/safeStorage";
import { readHotel, flushPendingHotelSync } from "../lib/hotelStore";
import { sendRideRequest } from "../lib/sendRideRequest";
import SaveHotelModal from "../components/SaveHotelModal";

const HISTORY_KEY = "habano.ride.history";
const HISTORY_MAX = 3;

/* Read past ride destinations from localStorage (newest first). */
function readHistory() {
  try {
    const raw = lsGet(HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, HISTORY_MAX) : [];
  } catch {
    return [];
  }
}

/* Persist a destination to history (deduped by name, newest first). */
export function pushHistory(dest) {
  if (!dest || !dest.name) return;
  try {
    const prev = readHistory();
    const filtered = prev.filter((d) => (d.name || "").toLowerCase() !== dest.name.toLowerCase());
    const next = [{
      name: dest.name,
      address: dest.address || "",
      lat: dest.lat ?? null,
      lng: dest.lng ?? null,
      at: new Date().toISOString(),
    }, ...filtered].slice(0, HISTORY_MAX);
    lsSet(HISTORY_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

export default function Car() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [savedBiz, setSavedBiz] = useState([]);          // [{id,name,address,lat,lng,category}]
  const [customLocations, setCustomLocations] = useState([]); // [{id,name,address,lat,lng}]
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  // iter134 — tap on hotel card opens the Save My Hotel sheet
  // (instead of firing a ride to the hotel).
  const [hotelSheetOpen, setHotelSheetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHistory(readHistory());
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      setUser(me);
      // iter137 — Read the unified hotel store first; it always wins for
      // the Car page since users may save offline.
      const localHotel = readHotel();
      if (localHotel) setHotel(localHotel);
      if (!me) {
        if (!localHotel) setHotel(null);
        return;
      }
      flushPendingHotelSync(true).catch(() => {});
      // Hotel — first hotel or airbnb in saved-locations (fallback only)
      api.get("/me/saved-locations").then(({ data }) => {
        if (cancelled) return;
        if (readHotel()) return; // local store wins
        const list = data || [];
        setHotel(list.find((s) => s.category === "hotel" || s.category === "airbnb") || null);
      }).catch(() => {});

      // Single /favorites call — split into businesses + custom_locations.
      const favoritesP = api.get("/favorites").then(({ data }) => data || []).catch(() => []);

      // Hydrate saved businesses
      favoritesP.then(async (favs) => {
        if (cancelled) return;
        const biz = favs.filter((f) => f.target_type === "business").slice(0, 12);
        const rows = await Promise.all(biz.map((f) =>
          api.get(`/businesses/${f.target_id}`).then(({ data: b }) => b).catch(() => null)
        ));
        if (!cancelled) setSavedBiz(rows.filter(Boolean));
      });

      // Custom locations — owned + saved-by-recipient (via same favorites payload)
      const own = api.get("/custom-locations").then(({ data }) =>
        (data || []).filter((d) => d.status === "active")
      ).catch(() => []);
      const savedCustom = favoritesP.then(async (favs) => {
        const clFavs = favs.filter((f) => f.target_type === "custom_location");
        const rows = await Promise.all(clFavs.map((f) =>
          api.get(`/custom-locations/${f.target_id}`).then(({ data: c }) => c).catch(() => null)
        ));
        return rows.filter(Boolean);
      });
      Promise.all([own, savedCustom]).then(([a, b]) => {
        if (cancelled) return;
        // Dedupe by id
        const map = new Map();
        [...a, ...b].forEach((row) => { if (row && row.id) map.set(row.id, row); });
        setCustomLocations(Array.from(map.values()));
      });
    })();
    return () => { cancelled = true; };
  }, []);

  /* Combined list filtered by local search (case-insensitive). */
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const items = [];
    if (hotel) {
      const name = (hotel.name || "").toLowerCase();
      const addr = (hotel.address || "").toLowerCase();
      if (name.includes(q) || addr.includes(q)) {
        items.push({ kind: "hotel", name: hotel.name, address: hotel.address, lat: hotel.lat, lng: hotel.lng });
      }
    }
    savedBiz.forEach((b) => {
      if ((b.name || "").toLowerCase().includes(q) || (b.address || "").toLowerCase().includes(q)) {
        items.push({ kind: "business", id: b.id, name: b.name, address: b.address, lat: b.lat, lng: b.lng });
      }
    });
    customLocations.forEach((c) => {
      if ((c.name || "").toLowerCase().includes(q) || (c.address || "").toLowerCase().includes(q)) {
        items.push({ kind: "custom", id: c.id, name: c.name, address: c.address, lat: c.lat, lng: c.lng });
      }
    });
    return items.slice(0, 8);
  }, [search, hotel, savedBiz, customLocations]);

  const openRide = (dest) => {
    if (!dest || !dest.name) return;
    pushHistory(dest);
    setHistory(readHistory());
    sendRideRequest(dest, { source: "car-hub" });
  };

  return (
    <main data-testid="car-page" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      {/* Header */}
      <header className="px-5 pt-8 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div
            data-testid="car-eyebrow"
            style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}
          >
            {t("car_eyebrow")}
          </div>
          <h1
            data-testid="car-title"
            className="mt-2"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 38, color: "#F5F1E8", lineHeight: 1.1, letterSpacing: "-0.01em" }}
          >
            {t("car_where")}{" "}
            <span style={{ color: "#E8B84B", fontStyle: "italic", fontWeight: 500 }}>{t("car_to")}</span>
          </h1>
        </div>
        <div className="pt-1">
          <HeaderActions user={user} testidPrefix="car" />
        </div>
      </header>

      {/* Search */}
      <section className="px-5 mt-5">
        <label
          className="flex items-center gap-2"
          style={{ background: "#141414", border: "1px solid rgba(232,184,75,0.25)", borderRadius: 14, padding: "0 14px", minHeight: 52 }}
        >
          <Search className="w-4 h-4" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
          <input
            type="text"
            data-testid="car-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("car_search_ph")}
            className="flex-1 bg-transparent outline-none"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", border: "none", height: 48 }}
          />
        </label>

        {searchResults.length > 0 && (
          <div
            data-testid="car-search-results"
            className="mt-2"
            style={{ background: "#141414", border: "1px solid rgba(232,184,75,0.2)", borderRadius: 12, overflow: "hidden" }}
          >
            {searchResults.map((r, i) => (
              <button
                key={`${r.kind}-${r.id || i}`}
                type="button"
                data-testid={`car-search-result-${i}`}
                onClick={() => { setSearch(""); openRide(r); }}
                className="w-full text-left flex items-center gap-3 px-4 hover:bg-white/[0.03]"
                style={{ minHeight: 56, borderBottom: i < searchResults.length - 1 ? "0.5px solid rgba(232,184,75,0.12)" : "none" }}
              >
                <ResultIcon kind={r.kind} />
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#F5F1E8", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.name}
                  </div>
                  {r.address && (
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#B0AAA3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.address}
                    </div>
                  )}
                </div>
                <CarIcon className="w-4 h-4" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Find on Map — iter88: moved up to sit directly under the search bar */}
      <section className="px-5 mt-5">
        <button
          type="button"
          data-testid="car-find-on-map"
          onClick={() => navigate("/car/map")}
          className="w-full flex items-center gap-4 transition-all hover:opacity-95"
          style={{
            background: "#141414",
            border: "1.5px solid rgba(232,184,75,0.45)",
            borderRadius: 16,
            padding: "16px 18px",
            minHeight: 80,
          }}
        >
          <span
            className="grid place-items-center rounded-full shrink-0"
            style={{ width: 44, height: 44, background: "rgba(232,184,75,0.15)", border: "1px solid rgba(232,184,75,0.4)" }}
          >
            <MapIcon className="w-5 h-5" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0 text-left">
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#F5F1E8", lineHeight: 1.15 }}>
              {t("car_find_on_map")}
            </div>
            <div className="mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3" }}>
              {t("car_find_on_map_sub")}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 shrink-0" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
        </button>
      </section>

      {/* Staying At — hotel card */}
      <section className="px-5 mt-7">
        <Eyebrow>{t("car_staying_at")}</Eyebrow>
        {hotel ? (
          <button
            type="button"
            data-testid="car-hotel-card"
            onClick={() => setHotelSheetOpen(true)}
            className="w-full text-left flex items-center gap-4 transition-all hover:opacity-95"
            style={{
              background: "linear-gradient(135deg, rgba(232,184,75,0.10) 0%, rgba(232,184,75,0.03) 100%)",
              border: "1.5px solid rgba(232,184,75,0.45)",
              borderRadius: 16,
              padding: "16px 18px",
              minHeight: 80,
              boxShadow: "0 4px 16px -8px rgba(232,184,75,0.35)",
            }}
          >
            <span
              className="grid place-items-center rounded-full shrink-0"
              style={{ width: 44, height: 44, background: "rgba(232,184,75,0.15)", border: "1px solid rgba(232,184,75,0.4)" }}
            >
              <Bed className="w-5 h-5" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 19, color: "#F5F1E8", lineHeight: 1.15 }}>
                {hotel.name || (lang === "es" ? "Mi Hotel" : "My Hotel")}
              </div>
              {hotel.address && (
                <div className="mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {hotel.address}
                </div>
              )}
            </div>
            <CarIcon className="w-5 h-5 shrink-0" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
          </button>
        ) : (
          <button
            type="button"
            data-testid="car-hotel-save-prompt"
            onClick={() => setHotelSheetOpen(true)}
            className="w-full flex items-center gap-3"
            style={{
              background: "#141414",
              border: "1px dashed rgba(232,184,75,0.4)",
              borderRadius: 16,
              padding: "16px 18px",
              minHeight: 72,
              textAlign: "left",
            }}
          >
            <span
              className="grid place-items-center rounded-full shrink-0"
              style={{ width: 40, height: 40, background: "rgba(232,184,75,0.1)" }}
            >
              <Bed className="w-5 h-5" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#F5F1E8", fontWeight: 500 }}>
                {t("car_save_hotel_prompt")}
              </div>
              <div className="mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#E8B84B", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600 }}>
                {t("car_save_hotel_cta")} →
              </div>
            </div>
          </button>
        )}
      </section>

      {/* Saved Places — horizontal scroll */}
      <section className="mt-8" data-testid="car-saved-places-section">
        <div className="px-5">
          <Eyebrow>{t("car_saved_places")}</Eyebrow>
        </div>
        {(savedBiz.length + customLocations.length) === 0 ? (
          <div className="px-5">
            <div
              data-testid="car-saved-empty"
              style={{ background: "#141414", border: "0.5px solid rgba(232,184,75,0.15)", borderRadius: 12, padding: "14px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3" }}
            >
              {t("car_no_saved_places")}
            </div>
          </div>
        ) : (
          <div
            data-testid="car-saved-scroll"
            className="overflow-x-auto no-scrollbar"
            style={{ paddingLeft: 20, paddingRight: 20 }}
          >
            <div className="flex gap-3" style={{ minWidth: "max-content" }}>
              {savedBiz.map((b) => (
                <SavedTile
                  key={`b-${b.id}`}
                  testid={`car-saved-biz-${b.id}`}
                  Icon={b.category === "hotel" ? Bed : MapPin}
                  name={b.name}
                  address={b.address}
                  onClick={() => openRide({ name: b.name, address: b.address || "", lat: b.lat, lng: b.lng })}
                />
              ))}
              {customLocations.map((c) => (
                <SavedTile
                  key={`c-${c.id}`}
                  testid={`car-saved-custom-${c.id}`}
                  Icon={Gem}
                  name={c.name}
                  address={c.address}
                  onClick={() => openRide({ name: c.name, address: c.address || "", lat: c.lat, lng: c.lng })}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Recent Rides */}
      {history.length > 0 && (
        <section className="px-5 mt-8" data-testid="car-recent-rides-section">
          <Eyebrow>{t("car_recent_rides")}</Eyebrow>
          <div
            style={{ background: "#141414", border: "0.5px solid rgba(232,184,75,0.15)", borderRadius: 14, overflow: "hidden" }}
          >
            {history.map((h, i) => (
              <button
                key={`h-${i}-${h.name}`}
                type="button"
                data-testid={`car-recent-${i}`}
                onClick={() => openRide(h)}
                className="w-full text-left flex items-center gap-3 hover:bg-white/[0.03]"
                style={{ minHeight: 60, padding: "12px 16px", borderBottom: i < history.length - 1 ? "0.5px solid rgba(232,184,75,0.1)" : "none" }}
              >
                <Clock className="w-4 h-4 shrink-0" style={{ color: "#B0AAA3" }} strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#F5F1E8", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {h.name}
                  </div>
                  {h.address && (
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#B0AAA3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {h.address}
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
              </button>
            ))}
          </div>
        </section>
      )}

      <SaveHotelModal
        isOpen={hotelSheetOpen}
        onClose={() => setHotelSheetOpen(false)}
        authed={!!user}
        onSaved={(saved) => {
          setHotel(saved);
          setHotelSheetOpen(false);
        }}
      />

      <BottomNav />
    </main>
  );
}

function Eyebrow({ children }) {
  return (
    <div
      className="mb-3"
      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E8B84B", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}
    >
      {children}
    </div>
  );
}

function ResultIcon({ kind }) {
  const Icon = kind === "hotel" ? Bed : kind === "custom" ? Gem : MapPin;
  return (
    <span
      className="grid place-items-center rounded-full shrink-0"
      style={{ width: 36, height: 36, background: "rgba(232,184,75,0.12)" }}
    >
      <Icon className="w-4 h-4" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
    </span>
  );
}

function SavedTile({ testid, Icon, name, address, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="text-left transition-all hover:opacity-95 shrink-0"
      style={{
        width: 180,
        background: "#141414",
        border: "1px solid rgba(232,184,75,0.2)",
        borderRadius: 14,
        padding: 14,
        minHeight: 130,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <span
        className="grid place-items-center rounded-full"
        style={{ width: 36, height: 36, background: "rgba(232,184,75,0.12)" }}
      >
        <Icon className="w-4 h-4" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
      </span>
      <div className="mt-3 min-w-0">
        <div
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            color: "#F5F1E8",
            fontWeight: 600,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.25,
          }}
        >
          {name}
        </div>
        {address && (
          <div
            className="mt-1"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {address}
          </div>
        )}
      </div>
    </button>
  );
}
